/**
 * The state badges on a Log row: Cancelled, Via LinkedIn, Past, Moved n×, Event deleted in calendar,
 * Not in Lever.
 *
 * Split out of InterviewsView for its 500-line ceiling (Sep 11 2026). Each badge answers a
 * question the row's own text cannot: whether this interview still exists, whether it has already
 * happened, whether it was moved, and whether Lever knows about it.
 */
import type { InterviewRow } from '../services/interviewsApi';

const PILL = 'rounded-full px-2 py-0.5 text-[11px] font-medium';

export function InterviewRowBadges({ row }: { row: InterviewRow }) {
  const cancelled = row.status === 'cancelled';
  return (
    <>
      {cancelled ? <span className={`${PILL} bg-red-100 text-red-700`}>Cancelled</span> : null}
      {/*
        Booked through a LinkedIn link (Oct 2026): nobody invited this person, and they may not be
        in Lever yet. When Lever did not take them, the row's "⚠ Not in Lever" says so and why.
      */}
      {row.source === 'linkedin-share' ? (
        <span
          className={`${PILL} bg-[#DBEBFC] text-[#0C447C]`}
          title="Booked through the role's LinkedIn link, not from an invite"
        >
          Via LinkedIn
        </span>
      ) : null}
      {/*
        Past (Sofia, Sep 11 2026). Without it the only sign an interview had already happened was
        the ABSENCE of Move and Cancel on the row, which reads as a broken page rather than as
        "this is over". It is driven by `upcoming` — the server's own field, the same one those
        buttons are gated on — so the badge and the missing buttons can never disagree. A cancelled
        row is exempt: it already says Cancelled, and two greyed-out pills say less than one.
      */}
      {!cancelled && row.start && !row.upcoming ? (
        <span
          className={`${PILL} bg-gray-100 text-gray-600`}
          title="This interview has already taken place — it can no longer be moved or cancelled."
        >
          Past
        </span>
      ) : null}
      {row.rescheduleCount > 0 ? (
        <span className={`${PILL} bg-[#FCEEE2] text-[#854F0B]`}>Moved {row.rescheduleCount}×</span>
      ) : null}
      {/*
        Set by the calendar sync (Oct 2026): the panel event was deleted in Google Calendar but the
        booking is still live. Deliberately a badge and not a cancellation — an accidental delete in
        the calendar must not cancel a real interview and email the candidate. A human decides.
      */}
      {!cancelled && row.calendarEventMissing ? (
        <span
          className={`${PILL} bg-red-50 text-red-700`}
          title="The calendar event for this interview was deleted in Google Calendar, but the booking is still live. Cancel it here, or recreate the event."
        >
          Event deleted in calendar
        </span>
      ) : null}
      {!cancelled && !row.leverPanelId && row.opportunityId ? (
        <span
          className={`${PILL} bg-gray-100 text-gray-600`}
          title="The interview exists in the calendar but was not written to Lever"
        >
          Not in Lever
        </span>
      ) : null}
    </>
  );
}
